import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/leadUtils";

export default function VoidExpenseModal({ expense, onClose, onVoided }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (reason.trim().length < 10) { setError("Please add a reason (at least 10 characters)."); return; }
    setSubmitting(true);
    try {
      await api.patch(`/expenses/${expense.id}/void`, { reason: reason.trim() });
      toast.success("Expense voided");
      onVoided && onVoided();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not void expense.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(14,48,48,0.55)" }}
      data-testid="void-expense-modal"
    >
      <div className="bg-white p-6 w-[min(440px,94vw)]" style={{ borderRadius: 0 }} onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-3">VOID EXPENSE?</div>
        <p className="text-sm text-brand-muted mb-4">
          This will mark the expense as voided. It stays in the audit log but won't count toward totals.
        </p>

        <div className="border-l-2 pl-3 mb-4" style={{ borderColor: "var(--brand-border)" }}>
          <div className="text-sm font-medium" style={{ color: "var(--brand-ink)" }} data-testid="void-expense-summary">
            {fmtINR(expense.amount)} · {expense.category}
          </div>
          {expense.description && (
            <div className="text-xs text-brand-muted mt-0.5">{expense.description}</div>
          )}
          <div className="text-xs text-brand-muted mt-1">{fmtDate(expense.date)}</div>
        </div>

        <label className="block">
          <span className="kicker block mb-1.5">Reason · min 10 chars</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Wrong amount entered, duplicate row"
            className="w-full px-3 py-2 text-sm border border-brand-border"
            style={{ borderRadius: 0 }}
            data-testid="void-expense-reason"
          />
          <div className="text-[10px] text-brand-muted text-right mt-1">{reason.trim().length} / min 10 chars</div>
        </label>

        {error && <p className="text-xs text-rose-700 mt-2" data-testid="void-expense-error">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
            data-testid="void-expense-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || reason.trim().length < 10}
            className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
            style={{ background: "var(--brand-danger, #b91c1c)" }}
            data-testid="void-expense-confirm"
          >
            {submitting ? "Voiding…" : "Void expense"}
          </button>
        </div>
      </div>
    </div>
  );
}
